import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Pagination from "@material-ui/lab/Pagination";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import VisibilityIcon from "@material-ui/icons/Visibility";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import MemoryIcon from "@material-ui/icons/Memory";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filters: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  evidence: {
    maxWidth: 360,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  detailText: {
    whiteSpace: "pre-wrap",
    background: theme.palette.action.hover,
    borderRadius: 8,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  summaryCard: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(2),
    background: theme.palette.background.default,
  },
  summaryTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  infoGrid: {
    marginTop: theme.spacing(1),
  },
  infoBlock: {
    background: theme.palette.action.hover,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  },
  infoLabel: {
    display: "block",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  infoValue: {
    fontWeight: 600,
  },
  mutedNote: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  sectionSpacing: {
    marginTop: theme.spacing(2),
  },
  tabsRow: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  knowledgePanel: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    background: theme.palette.background.default,
    borderLeft: `4px solid ${theme.palette.success.main}`,
  },
  proposalPanel: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    background: "linear-gradient(180deg, rgba(25,118,210,0.04) 0%, rgba(25,118,210,0.01) 100%)",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  proposalTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  proposalScope: {
    marginTop: theme.spacing(0.75),
  },
  proposalColumn: {
    height: "100%",
    padding: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.background.paper,
  },
  proposalCard: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    background: theme.palette.background.default,
    marginBottom: theme.spacing(1.5),
  },
  proposalCardTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  proposalCardText: {
    color: theme.palette.text.secondary,
  },
  proposalFlowRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  proposalFlowChip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  proposalHighlights: {
    marginTop: theme.spacing(2),
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  emptyState: {
    padding: theme.spacing(5),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const statusLabels = {
  pendente_revisao: "Exceção pendente",
  aprovado: "Qualificado",
  rejeitado: "Descartado",
  arquivado: "Arquivado",
};

const riskLabels = {
  baixa: "Baixo",
  media: "Médio",
  alta: "Alto",
};

const qualityLabels = {
  otimo: "Ótimo",
  bom: "Bom",
  razoavel: "Razoável",
  ruim: "Ruim",
  pendente: "Aguardando classificação",
};

const recommendationLabels = {
  aprovar: "Aprovar",
  aprovar_com_cautela: "Aprovar com cautela",
  rejeitar: "Rejeitar",
  arquivar: "Arquivar",
  analisar: "Classificar antes de decidir",
};

const categoryLabels = {
  qualificacao: "Qualificação",
  objecao: "Objeção",
  curso: "Curso",
  handoff: "Encaminhamento",
  fechamento: "Fechamento",
  tom: "Tom",
  processo: "Processo",
  erro: "Erro",
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("pt-BR");
};

const formatDuration = (seconds) => {
  if (seconds == null) return "-";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}min`;
  return `${Math.round(seconds / 3600)}h`;
};

const safeText = (value, fallback = "-") => {
  const text = String(value || "").trim();
  return text || fallback;
};

const getSnapshot = (candidate) => candidate?.metadata?.snapshot || {};
const getReviewAssistant = (candidate) => candidate?.metadata?.reviewAssistant || {};
const formatQualityChipLabel = (reviewAssistant) => {
  const label =
    qualityLabels[reviewAssistant?.qualityLabel] ||
    reviewAssistant?.qualityLabel ||
    "Sem nota";

  if (reviewAssistant?.qualityScore == null) {
    return label;
  }

  return `${label} • ${reviewAssistant.qualityScore}/100`;
};

const buildReviewSummary = (candidate) => {
  const snapshot = getSnapshot(candidate);
  const reviewAssistant = getReviewAssistant(candidate);
  const counts = snapshot.counts || {};
  const tracking = snapshot.tracking || {};
  const ticket = snapshot.ticket || {};
  const contact = snapshot.contact || {};
  const userData = snapshot.user || {};
  const queue = snapshot.queue || {};
  const whatsapp = snapshot.whatsapp || {};
  const rating = snapshot.rating || {};
  const signals = Array.isArray(snapshot.signals) ? snapshot.signals : [];
  const logsByType = snapshot.logs?.countsByType || {};
  const logKeys = Object.keys(logsByType);

  const learningFocus =
    safeText(candidate?.proposedChange) ||
    safeText(snapshot.summary) ||
    "Manter o padrão observado neste atendimento para futuros leads.";

  const executiveSummary = safeText(snapshot.summary, "Snapshot ainda não disponível para este candidato.");
  const impact =
    candidate?.riskLevel === "alta"
      ? "Aprovar com cautela e manter supervisão próxima."
      : "Pode seguir como aprendizado supervisionado da LYRA após aprovação.";

  const finalImpact =
    reviewAssistant?.classificationStatus === "pendente_classificacao"
      ? "Esta observação ainda precisa de classificação antes da decisão final."
      : reviewAssistant?.classificationStatus === "pre_classificado"
      ? "Esta observação já recebeu pré-classificação automática do agente e pode seguir para classificação final do admin."
      : reviewAssistant?.recommendedAction === "rejeitar"
      ? "O agente recomenda não transformar este padrão em memória da LYRA."
      : reviewAssistant?.recommendedAction === "aprovar_com_cautela" || candidate?.riskLevel === "alta"
        ? "Aprovar com cautela e manter supervisão próxima."
        : impact;

  const contextItems = [
    { label: "Contato", value: safeText(contact.name) },
    { label: "Atendente", value: safeText(userData.name) },
    { label: "Fila", value: safeText(queue.name) },
    { label: "Canal", value: safeText(whatsapp.name || whatsapp.channel) },
    { label: "Ticket", value: ticket.id ? `#${ticket.id}` : "-" },
    { label: "Status", value: safeText(ticket.status) },
  ];

  const metricItems = [
    { label: "Mensagens", value: counts.messages ?? 0 },
    { label: "Lead", value: counts.customerMessages ?? 0 },
    { label: "Consultor", value: counts.agentMessages ?? 0 },
    { label: "Notas", value: counts.notes ?? 0 },
    { label: "Logs", value: counts.logs ?? 0 },
    { label: "NPS", value: rating.rate ?? "-" },
    { label: "1ª resposta", value: formatDuration(tracking.firstResponseSeconds) },
    { label: "Tempo total", value: formatDuration(tracking.supportSeconds) },
    { label: "Espera", value: formatDuration(tracking.waitSeconds) },
  ];

  return {
    learningFocus,
    executiveSummary,
    impact: finalImpact,
    reviewAssistant,
    contextItems,
    metricItems,
    signals,
    logsByType,
    logKeys,
    transcript: safeText(snapshot.transcript, ""),
    notes: Array.isArray(snapshot.notes) ? snapshot.notes : [],
  };
};

const LyraLearning = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [viewMode, setViewMode] = useState("revisao");
  const [selectedStatus, setSelectedStatus] = useState("pendente_revisao");
  const [selectedUser, setSelectedUser] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [classifyingCandidateId, setClassifyingCandidateId] = useState(null);
  const [classifyingUserId, setClassifyingUserId] = useState(null);
  const [selectedUserBatchStatus, setSelectedUserBatchStatus] = useState(null);
  const [evolutionProposal, setEvolutionProposal] = useState(null);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisTicketRef, setAnalysisTicketRef] = useState("");
  const [analysisUserId, setAnalysisUserId] = useState("");
  const previousBatchStatusRef = useRef(null);
  const pageSize = 20;

  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  const fetchSupportData = useCallback(async () => {
    try {
      const [{ data: usersData }, { data: agentsData }] = await Promise.all([
        api.get("/users/list"),
        api.get("/lyra/user-agents"),
      ]);
      setUsers(usersData || []);
      setAgents(agentsData || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lyra/learning-candidates", {
        params: {
          pageNumber,
          status: selectedStatus,
          userId: selectedUser || undefined,
          searchParam: searchParam || undefined,
        },
      });
      setCandidates(data.candidates || []);
      setCount(data.count || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, searchParam, selectedStatus, selectedUser]);

  const fetchSelectedUserBatchStatus = useCallback(
    async (userIdParam = selectedUser) => {
      if (!userIdParam || user?.profile !== "admin") {
        setSelectedUserBatchStatus(null);
        return null;
      }

      const { data } = await api.get("/lyra/learning-candidates/classify-by-user/status", {
        params: {
          userId: userIdParam,
        },
      });

      setSelectedUserBatchStatus(data || null);
      return data || null;
    },
    [selectedUser, user?.profile]
  );

  const fetchEvolutionProposal = useCallback(
    async (userIdParam = selectedUser) => {
      setProposalLoading(true);
      try {
        const { data } = await api.get("/lyra/evolution/proposal", {
          params: userIdParam ? { userId: userIdParam } : undefined,
        });

        setEvolutionProposal(data || null);
      } catch (err) {
        setEvolutionProposal(null);
        toastError(err);
      } finally {
        setProposalLoading(false);
      }
    },
    [selectedUser]
  );

  useEffect(() => {
    fetchSupportData();
  }, [fetchSupportData]);

  useEffect(() => {
    const timer = setTimeout(fetchCandidates, 350);
    return () => clearTimeout(timer);
  }, [fetchCandidates]);

  useEffect(() => {
    fetchSelectedUserBatchStatus().catch(() => {
      setSelectedUserBatchStatus(null);
    });
  }, [fetchSelectedUserBatchStatus]);

  useEffect(() => {
    fetchEvolutionProposal().catch(() => {
      setEvolutionProposal(null);
    });
  }, [fetchEvolutionProposal]);

  useEffect(() => {
    if (!selectedUserBatchStatus || selectedUserBatchStatus.status !== "running" || !selectedUser) {
      return undefined;
    }

    const interval = setInterval(async () => {
      try {
        await fetchSelectedUserBatchStatus(selectedUser);
      } catch (err) {
        // Mantem o lote rodando e deixa a proxima tentativa atualizar a tela.
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [fetchSelectedUserBatchStatus, selectedUser, selectedUserBatchStatus]);

  useEffect(() => {
    const previousStatus = previousBatchStatusRef.current;
    const currentStatus = selectedUserBatchStatus?.status || null;

    if (previousStatus === "running" && currentStatus && currentStatus !== "running") {
      fetchCandidates();
      if ((selectedUserBatchStatus?.failed || 0) > 0) {
        toast.warn(selectedUserBatchStatus?.message || "Classificação em lote concluída com falhas.");
      } else if ((selectedUserBatchStatus?.classified || 0) > 0) {
        toast.success(selectedUserBatchStatus?.message || "Classificação em lote concluída com sucesso.");
      }
    }

    previousBatchStatusRef.current = currentStatus;
  }, [fetchCandidates, selectedUserBatchStatus]);

  const handleStatusChange = async (candidate, status, reviewDecisionMode = "") => {
    try {
      await api.put(`/lyra/learning-candidates/${candidate.id}/status`, {
        status,
        reviewNotes,
        reviewDecisionMode,
      });
      const successMessage =
        status === "aprovado"
          ? reviewDecisionMode === "cautela"
            ? "Aprendizado aprovado com cautela para uso supervisionado."
            : "Aprendizado aprovado para uso supervisionado."
          : status === "arquivado"
            ? "Aprendizado arquivado."
            : "Aprendizado rejeitado.";
      toast.success(successMessage);
      if (status === "aprovado") {
        setViewMode("conhecimento");
        setSelectedStatus("aprovado");
      }
      setSelectedCandidate(null);
      setReviewNotes("");
      fetchCandidates();
      fetchEvolutionProposal();
    } catch (err) {
      toastError(err);
    }
  };

  const handleAnalyzeTicket = async () => {
    const ticketRef = analysisTicketRef.trim();
    if (!ticketRef) {
      toast.warn("Informe o ID ou UUID do ticket para análise.");
      return;
    }

    setAnalysisLoading(true);
    try {
      const { data } = await api.post(`/lyra/tickets/${encodeURIComponent(ticketRef)}/analyze`, {
        userId: analysisUserId || undefined,
      });

      if (data.discarded) {
        toast.info(data.reason || "Ticket descartado: não identificado como atendimento de lead.");
      } else {
        toast.success(
          data.candidatesCreated > 0
            ? `${data.candidatesCreated} aprendizado(s) criado(s) para revisão.`
            : "Análise concluída. Nenhum novo aprendizado foi sugerido."
        );
      }
      setAnalysisOpen(false);
      setAnalysisTicketRef("");
      setAnalysisUserId("");
      setSelectedStatus("pendente_revisao");
      setSelectedUser(data.userId || "");
      setPageNumber(1);
      fetchCandidates();
    } catch (err) {
      toastError(err);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleClassifyCandidate = async (candidate, keepModalOpen = false) => {
    try {
      setClassifyingCandidateId(candidate.id);
      const { data } = await api.post(`/lyra/learning-candidates/${candidate.id}/classify`);
      toast.success("Candidato classificado com sucesso.");
      if (keepModalOpen && data?.candidate) {
        setSelectedCandidate(data.candidate);
      }
      fetchCandidates();
    } catch (err) {
      toastError(err);
    } finally {
      setClassifyingCandidateId(null);
    }
  };

  const handleClassifySelectedUser = async () => {
    if (!selectedUser) {
      toast.warn("Selecione um usuário para reclassificar os casos dele.");
      return;
    }

    try {
      setClassifyingUserId(Number(selectedUser));
      const { data } = await api.post("/lyra/learning-candidates/classify-by-user", {
        userId: selectedUser,
      });
      setSelectedUserBatchStatus(data || null);

      if (data?.status === "running") {
        toast.success(data?.message || "Classificação em lote iniciada.");
      } else {
        toast.info(data?.message || "Nenhum candidato pendente de classificação foi encontrado para este usuário.");
      }

      setPageNumber(1);
      await fetchSelectedUserBatchStatus(selectedUser);
    } catch (err) {
      toastError(err);
    } finally {
      setClassifyingUserId(null);
    }
  };

  const agentByUserId = (userId) =>
    agents.find((agent) => Number(agent.userId) === Number(userId));

  const reviewSummary = selectedCandidate ? buildReviewSummary(selectedCandidate) : null;
  const isKnowledgeView = viewMode === "conhecimento";
  const selectedUserData = users.find((item) => Number(item.id) === Number(selectedUser));
  const isUserBatchRunning =
    Number(selectedUserBatchStatus?.userId || 0) === Number(selectedUser || 0) &&
    selectedUserBatchStatus?.status === "running";
  const proposalScopeLabel =
    evolutionProposal?.scope?.type === "usuario"
      ? `Usuário: ${evolutionProposal?.scope?.userName || selectedUserData?.name || "selecionado"}`
      : evolutionProposal?.scope?.companyId === 5
        ? "Empresa 5"
        : "Escopo: empresa inteira";

  const displayedProposalFlow =
    Array.isArray(evolutionProposal?.finalProposalFlow) && evolutionProposal.finalProposalFlow.length > 0
      ? evolutionProposal.finalProposalFlow
      : evolutionProposal?.proposedFlow || [];
  const hasCustomFinalProposal = Boolean(evolutionProposal?.finalProposalMeta?.hasCustomFlow);
  const hasFinalProposalVersion = Boolean(evolutionProposal?.savedProposal?.id || evolutionProposal?.finalProposalMeta?.editedAt);
  const proposalVersionLabel = hasCustomFinalProposal
    ? "Versão final editada"
    : hasFinalProposalVersion
      ? "Versão final salva"
      : "Versão gerada";
  const finalProposalNotes =
    String(evolutionProposal?.finalProposalNotes || evolutionProposal?.savedProposal?.notes || "").trim();

  return (
    <MainContainer>
      <MainHeader>
        <Title>Aprendizado LYRA</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<MemoryIcon />}
            onClick={() => history.push("/lyra-evolution")}
          >
            Evolução LYRA
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<MemoryIcon />}
            onClick={() => setAnalysisOpen(true)}
          >
            Analisar ticket
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchSupportData();
              fetchCandidates();
            }}
          >
            Atualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <Tabs
          value={viewMode}
          onChange={(event, value) => {
            setViewMode(value);
            setPageNumber(1);
            setSelectedStatus(value === "conhecimento" ? "aprovado" : "pendente_revisao");
          }}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabsRow}
        >
          <Tab value="revisao" label="Exceções de revisão" />
          <Tab value="conhecimento" label="Conhecimento qualificado" />
        </Tabs>

        {isKnowledgeView && (
          <Paper variant="outlined" className={classes.knowledgePanel}>
            <Typography variant="h6">Painel de conhecimento da LYRA</Typography>
            <Typography variant="body2" color="textSecondary">
              Os aprendizados aprovados pelo admin ficam registrados aqui como base supervisionada para futuras análises.
            </Typography>
          </Paper>
        )}

        <Paper variant="outlined" className={classes.proposalPanel}>
          <Box className={classes.proposalTopRow}>
            <Box>
              <Typography variant="h6">Proposta de evolução da LYRA</Typography>
              <Typography variant="body2" color="textSecondary">
                Esta visão compara o fluxo atual com a proposta montada a partir dos aprendizados já aprovados.
              </Typography>
              <Typography variant="caption" color="textSecondary" className={classes.proposalScope}>
                {proposalScopeLabel}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={() => fetchEvolutionProposal()}
              disabled={proposalLoading}
            >
              {proposalLoading ? "Gerando..." : "Atualizar proposta"}
            </Button>
          </Box>

          {proposalLoading && !evolutionProposal ? (
            <Typography variant="body2" color="textSecondary">
              Consolidando aprendizados aprovados para montar a comparação...
            </Typography>
          ) : evolutionProposal?.ok ? (
            <>
              <Box className={classes.proposalFlowRow}>
                <Chip size="small" color="primary" label={`${evolutionProposal.summary?.totalApproved || 0} aprovados`} />
                <Chip size="small" variant="outlined" label={`${evolutionProposal.summary?.categories || 0} categorias`}/>
                <Chip size="small" variant="outlined" label={`${evolutionProposal.summary?.flowSteps || 0} blocos detalhados`}/>
                <Chip size="small" variant="outlined" label={evolutionProposal.scope?.type === "usuario" ? "Comparação por usuário" : "Comparação da empresa"} />
                <Chip size="small" variant="outlined" label={proposalVersionLabel} />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" className={classes.proposalColumn}>
                    <Typography variant="subtitle1" color="primary">
                      Como está hoje
                    </Typography>
                    {Array.isArray(evolutionProposal.currentFlow) &&
                      evolutionProposal.currentFlow.map((step) => (
                        <Box key={step.id} className={classes.proposalCard}>
                          <Typography className={classes.proposalCardTitle}>{step.title}</Typography>
                          <Typography variant="body2" className={classes.proposalCardText}>
                            {step.current}
                          </Typography>
                        </Box>
                      ))}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" className={classes.proposalColumn}>
                    <Typography variant="subtitle1" color="primary">
                      Como pode ficar
                    </Typography>
                    {Array.isArray(displayedProposalFlow) &&
                      displayedProposalFlow.map((step) => (
                        <Box key={step.id} className={classes.proposalCard}>
                          <Typography className={classes.proposalCardTitle}>{step.title}</Typography>
                          <Typography variant="body2" className={classes.proposalCardText}>
                            {step.proposed}
                          </Typography>
                          <Box mt={1} display="flex" flexWrap="wrap" gridGap={8}>
                            {(step.sourceCount || 0) > 0 && (
                              <Chip size="small" color="primary" label={`${step.sourceCount} aprendizados aprovados`} />
                            )}
                          </Box>
                        </Box>
                      ))}
                  </Paper>
                </Grid>
              </Grid>

              {hasFinalProposalVersion && (
                <Paper variant="outlined" className={classes.proposalCard} style={{ marginTop: 16 }}>
                  <Typography className={classes.proposalCardTitle}>Versão final do admin</Typography>
                  <Typography variant="body2" className={classes.proposalCardText}>
                    A proposta abaixo já considera a última revisão salva para a empresa 5. Use este painel para
                    validar o que foi efetivamente preparado para aplicação.
                  </Typography>
                  {finalProposalNotes && (
                    <Box mt={1}>
                      <Typography variant="caption" color="textSecondary">
                        Observações do admin:
                      </Typography>
                      <Typography variant="body2" className={classes.proposalCardText}>
                        {finalProposalNotes}
                      </Typography>
                    </Box>
                  )}
                  {evolutionProposal?.finalProposalMeta?.editedAt && (
                    <Typography variant="caption" color="textSecondary">
                      Editado em {formatDate(evolutionProposal.finalProposalMeta.editedAt)}
                    </Typography>
                  )}
                </Paper>
              )}

              <Box className={classes.proposalHighlights}>
                <Typography variant="subtitle2">Aprendizados que alimentam a proposta</Typography>
                <Box className={classes.proposalFlowRow} mt={1}>
                  {Array.isArray(evolutionProposal.categorySummary) &&
                    evolutionProposal.categorySummary.map((item) => (
                      <Chip
                        key={item.category}
                        size="small"
                        variant="outlined"
                        className={classes.proposalFlowChip}
                        label={`${categoryLabels[item.category] || item.title} (${item.approvedCount})`}
                      />
                    ))}
                </Box>

                <Grid container spacing={2}>
                  {Array.isArray(evolutionProposal.approvedHighlights) &&
                    evolutionProposal.approvedHighlights.slice(0, 4).map((item) => (
                      <Grid item xs={12} md={6} key={item.id}>
                        <Paper variant="outlined" className={classes.proposalCard}>
                          <Typography className={classes.proposalCardTitle}>{item.title}</Typography>
                          <Typography variant="body2" className={classes.proposalCardText}>
                            {item.evidence}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Sugestão aprovada: {item.proposedChange}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Nenhum aprendizado aprovado disponível para montar a proposta ainda.
            </Typography>
          )}
        </Paper>

        <Paper variant="outlined" className={classes.filters}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Pesquisar"
                value={searchParam}
                onChange={(e) => {
                  setSearchParam(e.target.value);
                  setPageNumber(1);
                }}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8 }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setPageNumber(1);
                  setViewMode(e.target.value === "aprovado" ? "conhecimento" : "revisao");
                }}
                label="Status"
              >
                  <MenuItem value="pendente_revisao">Exceção pendente</MenuItem>
                  <MenuItem value="aprovado">Aprovado</MenuItem>
                  <MenuItem value="rejeitado">Rejeitado</MenuItem>
                  <MenuItem value="arquivado">Arquivado</MenuItem>
                  <MenuItem value="todos">Todos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Usuário</InputLabel>
                <Select
                  value={selectedUser}
                  onChange={(e) => {
                    setSelectedUser(e.target.value);
                    setPageNumber(1);
                  }}
                  label="Usuário"
                  disabled={user?.profile !== "admin"}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {users.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" flexDirection="column" gridGap={8}>
                {user?.profile === "admin" && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<MemoryIcon />}
                    disabled={!selectedUser || classifyingUserId === Number(selectedUser) || isUserBatchRunning}
                    onClick={handleClassifySelectedUser}
                  >
                    {classifyingUserId === Number(selectedUser) || isUserBatchRunning
                      ? `Classificando ${selectedUserBatchStatus?.processed || 0}/${selectedUserBatchStatus?.total || 0}`
                      : selectedUserData
                        ? `Classificar ${selectedUserData.name}`
                        : "Classificar usuário"}
                  </Button>
                )}
                {selectedUserBatchStatus?.message && Number(selectedUserBatchStatus?.userId || 0) === Number(selectedUser || 0) && (
                  <Typography variant="caption" color="textSecondary">
                    {selectedUserBatchStatus.message}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary">
                  Cada candidato fica separado por usuário e passa primeiro pela qualificação automática dos agentes.
                  Só os casos fora da régua seguem para revisão excepcional do admin.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Usuário / agente</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Qualidade</TableCell>
              <TableCell>Risco</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Evidência</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={9} />
            ) : candidates.length ? (
              candidates.map((candidate) => {
                const owner = candidate.user;
                const agent = agentByUserId(candidate.userId || candidate.metadata?.userId);
                const reviewAssistant = getReviewAssistant(candidate);
                return (
                  <TableRow key={candidate.id}>
                    <TableCell>{formatDate(candidate.createdAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{owner?.name || candidate.metadata?.userName || "Sem usuário vinculado"}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {agent?.name || "Agente individual não localizado"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{candidate.whatsapp?.name || candidate.metadata?.whatsappName || candidate.sourceType}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Ticket {candidate.ticketId || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={categoryLabels[reviewAssistant.learningCategory] || reviewAssistant.learningCategory || "-"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={reviewAssistant.qualityLabel === "otimo" ? "primary" : "default"}
                        label={formatQualityChipLabel(reviewAssistant)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={riskLabels[candidate.riskLevel] || candidate.riskLevel} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={candidate.status === "aprovado" ? "primary" : "default"} label={statusLabels[candidate.status] || candidate.status} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={candidate.evidence || ""}>
                        <Typography className={classes.evidence} variant="body2">
                          {candidate.evidence}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {user?.profile === "admin" &&
                        candidate?.status === "pendente_revisao" &&
                        reviewAssistant?.classificationStatus !== "classificado" && (
                          <Tooltip title="Reclassificar exceção">
                            <Button
                              size="small"
                              color="primary"
                              startIcon={<MemoryIcon />}
                              disabled={classifyingCandidateId === candidate.id}
                              onClick={() => handleClassifyCandidate(candidate)}
                            >
                              {classifyingCandidateId === candidate.id ? "Classificando..." : "Reclassificar exceção"}
                            </Button>
                          </Tooltip>
                        )}
                      <Tooltip title="Ver análise">
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<VisibilityIcon />}
                          onClick={() => {
                            setSelectedCandidate(candidate);
                            setReviewNotes(candidate.reviewNotes || "");
                          }}
                        >
                          Ver
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={9}>
                  <Box className={classes.emptyState}>Nenhum aprendizado encontrado para os filtros atuais.</Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box className={classes.pagination}>
          <Pagination
            count={totalPages}
            page={pageNumber}
            color="primary"
            onChange={(event, value) => setPageNumber(value)}
          />
        </Box>
      </Paper>

      <Dialog open={!!selectedCandidate} onClose={() => setSelectedCandidate(null)} maxWidth="md" fullWidth>
        <DialogTitle>Revisar aprendizado da LYRA</DialogTitle>
        <DialogContent dividers>
          {selectedCandidate && (
            <Grid container spacing={2}>
              {reviewSummary && (
                <Grid item xs={12}>
                  <Paper variant="outlined" className={classes.summaryCard}>
                    <Typography variant="subtitle2" color="primary" className={classes.summaryTitle}>
                      Resumo para aprovação do admin
                    </Typography>
                    <Typography variant="body1">
                      <strong>A LYRA deve aprender:</strong> {reviewSummary.learningFocus}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" className={classes.sectionSpacing}>
                      {reviewSummary.executiveSummary}
                    </Typography>
                    <Typography variant="body2" className={classes.mutedNote}>
                      Impacto esperado: {reviewSummary.impact}
                    </Typography>

                    <Box className={classes.chipRow}>
                      <Chip
                        size="small"
                        color="primary"
                        label={`Recomendação: ${
                          recommendationLabels[reviewSummary.reviewAssistant?.recommendedAction] ||
                          reviewSummary.reviewAssistant?.recommendedAction ||
                          "Sem recomendação"
                        }`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Qualidade: ${formatQualityChipLabel(reviewSummary.reviewAssistant)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Categoria: ${
                          categoryLabels[reviewSummary.reviewAssistant?.learningCategory] ||
                          reviewSummary.reviewAssistant?.learningCategory ||
                          "Não classificada"
                        }`}
                      />
                    </Box>

                    {reviewSummary.reviewAssistant?.adminReason && (
                      <Typography variant="body2" className={classes.mutedNote}>
                        Leitura do agente: {reviewSummary.reviewAssistant.adminReason}
                      </Typography>
                    )}

                    {Array.isArray(reviewSummary.reviewAssistant?.strengths) &&
                      reviewSummary.reviewAssistant.strengths.length > 0 && (
                        <Typography variant="body2" className={classes.mutedNote}>
                          Pontos fortes: {reviewSummary.reviewAssistant.strengths.join(" • ")}
                        </Typography>
                      )}

                    {Array.isArray(reviewSummary.reviewAssistant?.caveats) &&
                      reviewSummary.reviewAssistant.caveats.length > 0 && (
                        <Typography variant="body2" className={classes.mutedNote}>
                          Cuidados: {reviewSummary.reviewAssistant.caveats.join(" • ")}
                        </Typography>
                      )}

                    <Box className={classes.chipRow}>
                      {reviewSummary.signals.length ? (
                        reviewSummary.signals.map((signal) => (
                          <Chip key={signal} size="small" label={signal} variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Nenhum sinal adicional identificado no snapshot.
                        </Typography>
                      )}
                    </Box>

                    <Grid container spacing={2} className={classes.infoGrid}>
                      {reviewSummary.contextItems.map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item.label}>
                          <Box className={classes.infoBlock}>
                            <Typography className={classes.infoLabel}>{item.label}</Typography>
                            <Typography className={classes.infoValue}>{item.value}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Grid container spacing={2} className={classes.infoGrid}>
                      {reviewSummary.metricItems.map((item) => (
                        <Grid item xs={6} sm={4} md={3} key={item.label}>
                          <Box className={classes.infoBlock}>
                            <Typography className={classes.infoLabel}>{item.label}</Typography>
                            <Typography className={classes.infoValue}>{item.value}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    {reviewSummary.logKeys.length > 0 && (
                      <Typography variant="body2" className={classes.mutedNote}>
                        Logs mais frequentes:{" "}
                        {reviewSummary.logKeys.map((key) => `${key} (${reviewSummary.logsByType[key]})`).join(", ")}
                      </Typography>
                    )}

                    <Typography variant="body2" className={classes.mutedNote}>
                      Se qualificado, esse padrão entra como aprendizado supervisionado da LYRA. O admin acompanha a evolução e aprova a versão final.
                    </Typography>
                  </Paper>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Título</Typography>
                <Typography>{selectedCandidate.title}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Usuário</Typography>
                <Typography>{selectedCandidate.user?.name || selectedCandidate.metadata?.userName || "Sem usuário vinculado"}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Evidência do atendimento</Typography>
                <Typography className={classes.detailText}>{selectedCandidate.evidence}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Mudança sugerida</Typography>
                <Typography className={classes.detailText}>{selectedCandidate.proposedChange}</Typography>
              </Grid>
              {selectedCandidate?.status === "aprovado" && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Painel de conhecimento</Typography>
                  <Typography className={classes.detailText}>
                    Este aprendizado já foi aprovado e fica disponível na aba "Conhecimento aprovado" como base supervisionada da LYRA.
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  label="Observação da revisão"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCandidate(null)}>Fechar</Button>
          {user?.profile === "admin" && selectedCandidate?.status === "pendente_revisao" && (
            <>
              {reviewSummary?.reviewAssistant?.classificationStatus !== "classificado" && (
                <Button
                  color="primary"
                  variant="outlined"
                  startIcon={<MemoryIcon />}
                  disabled={classifyingCandidateId === selectedCandidate.id}
                  onClick={() => handleClassifyCandidate(selectedCandidate, true)}
                >
                  {classifyingCandidateId === selectedCandidate.id ? "Classificando..." : "Reclassificar exceção"}
                </Button>
              )}
              <Button
                variant="outlined"
                color="default"
                onClick={() => handleStatusChange(selectedCandidate, "arquivado")}
              >
                Arquivar
              </Button>
              <Button
                startIcon={<HighlightOffIcon />}
                onClick={() => handleStatusChange(selectedCandidate, "rejeitado", "descartar")}
              >
                Rejeitar
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() => handleStatusChange(selectedCandidate, "aprovado", "cautela")}
              >
                Aprovar com cautela
              </Button>
              <Button
                color="primary"
                variant="contained"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() => handleStatusChange(selectedCandidate, "aprovado", "padrao")}
              >
                Aprovar
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={analysisOpen} onClose={() => !analysisLoading && setAnalysisOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Analisar atendimento com IA</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                A análise cria candidatos de aprendizado separados por usuário. A qualificação automática decide o
                destino do caso e o admin só intervém nas exceções e na evolução final.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="ID ou UUID do ticket"
                value={analysisTicketRef}
                onChange={(e) => setAnalysisTicketRef(e.target.value)}
                disabled={analysisLoading}
                autoFocus
              />
            </Grid>
            {user?.profile === "admin" && (
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Usuário responsável</InputLabel>
                  <Select
                    value={analysisUserId}
                    onChange={(e) => setAnalysisUserId(e.target.value)}
                    label="Usuário responsável"
                    disabled={analysisLoading}
                  >
                    <MenuItem value="">Usar atendente do ticket</MenuItem>
                    {users.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalysisOpen(false)} disabled={analysisLoading}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<MemoryIcon />}
            onClick={handleAnalyzeTicket}
            disabled={analysisLoading}
          >
            {analysisLoading ? "Analisando..." : "Analisar"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default LyraLearning;
